import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Shared bottom-sheet / offcanvas shell used by every add/edit popup across the app.
 *
 * It owns the backdrop, the swipe-to-dismiss drag handle, the sticky header
 * (title + close button), the scroll body, the slide-up/slide-down animations and
 * the default Cancel/Save footer. Consumers project only their form body:
 *
 *   <app-offcanvas-sheet [visible]="visible" [title]="title"
 *                        (closeCanvas)="onClose()" (save)="onSave()">
 *     <div class="form-group-mobile">...</div>
 *   </app-offcanvas-sheet>
 *
 * Optional projection slots:
 *   [sheetHeaderExtra] - extra content rendered in the header before the close button (e.g. a badge)
 *   [sheetActions]     - replaces the default Cancel/Save footer
 *
 * NOTE: projected form fields are styled by the *consumer's* stylesheet (Angular view
 * encapsulation). Add 'offcanvas-sheet.shared.css' to each consumer's styleUrls so the
 * .form-group-mobile / .form-control styles match.
 */
@Component({
  selector: 'app-offcanvas-sheet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './offcanvas-sheet.component.html',
  styleUrls: ['./offcanvas-sheet.component.css'],
})
export class OffcanvasSheetComponent {
  @Input() visible = false;
  @Input() title = '';
  @Input() saveLabel = 'Save';
  @Input() cancelLabel = 'Cancel';
  @Input() saveDisabled = false;
  /** Set false to project a custom footer via the [sheetActions] slot. */
  @Input() showDefaultActions = true;

  @Output() closeCanvas = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  readonly Math = Math;

  isClosing = false;
  swipeDeltaY = 0;
  private swipeStartY = 0;
  private swipeSheetHeight = 0;
  swipeActive = false;

  onSave(): void {
    this.save.emit();
  }

  /** Plays the slide-down animation, then notifies the parent. */
  onClose(): void {
    if (this.isClosing) return;
    this.isClosing = true;
    this.swipeDeltaY = 0;
    setTimeout(() => {
      this.isClosing = false;
      this.closeCanvas.emit();
    }, 280);
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('offcanvas-backdrop')) {
      this.onClose();
    }
  }

  onHandleTouchStart(e: TouchEvent): void {
    this.swipeActive = true;
    this.swipeStartY = e.touches[0].clientY;
    const sheet = (e.currentTarget as HTMLElement).closest('.offcanvas-sheet') as HTMLElement;
    this.swipeSheetHeight = sheet?.offsetHeight ?? 300;
    this.swipeDeltaY = 0;
  }

  onHandleTouchMove(e: TouchEvent): void {
    if (!this.swipeActive) return;
    this.swipeDeltaY = Math.max(0, e.touches[0].clientY - this.swipeStartY);
  }

  onHandleTouchEnd(): void {
    if (!this.swipeActive) return;
    this.swipeActive = false;
    if (this.swipeDeltaY > this.swipeSheetHeight * 0.3) {
      this.swipeDeltaY = 0;
      this.onClose();
    } else {
      this.swipeDeltaY = 0;
    }
  }

  get swipeProgress(): number {
    return this.swipeSheetHeight === 0 ? 0 : Math.min(1, this.swipeDeltaY / (this.swipeSheetHeight * 0.3));
  }
}
